import { Component, computed, input, output, signal } from "@angular/core";
import { PaymentLineItemRestriction } from "../../quote-edit-modal/quote-edit-modal.service";
import { MatCardModule } from "@angular/material/card";
import { MatListModule } from "@angular/material/list";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTableModule } from "@angular/material/table";
import { CommonModule } from "@angular/common";
import { CommaSeparatedInputComponent } from "../../../components/comma-separated-input/comma-separated-input.component";
import { ScrollInsetShadowDirective } from "../../../directives/scroll-inset-shadow.directive";

interface TrialSummaryItem {
  TrialNumber: string,
  Summary: "Trial" | "Year" | "None",
  ObjectiveName: string,
  Year: number,
  Committed: number,
  Invoiced: number
};

export interface Trial {
  TrialNumber: string,
  Title: string,
  IsCommercial: boolean,
  Currency: string,
  Status: string,
  BudgetYears: Array<BudgetYear>,
  RestrictionStatus: PaymentLineItemRestriction,
  SourceSystemLink?: string,
  ItemType: number
}

export interface BudgetYear {
  Year: number,
  Objectives: Array<Objective>
}

export interface Objective {
  ObjectiveName: string,
  QuotedAmount: number,
  Invoices: Array<Invoice>,
  PaymentLineItemVendorAssocId: number,
  Currency: string
}

export interface Invoice {
  Amount: number,
  Name: string,
  Editable: boolean,
  IsCurrentInvoice: boolean,
  InvoiceId: number,
  PaymentLineItemInvoiceAssocId?: number
}

@Component({
  selector: 'invoice-payment-line-item-assoc',
  templateUrl: 'invoice-payment-line-item-assoc.component.html',
  styleUrl: 'invoice-payment-line-item-assoc.component.less',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatListModule, MatButtonModule, MatIconModule, MatTableModule,
    CommaSeparatedInputComponent, ScrollInsetShadowDirective]
})
export class InvoicePaymentLineItemAssocComponent {
  public dataChange = output<Trial[]>();
  public invoiceNumber = input<string>("Current Invoice");
  public invoiceId = input.required<number>();
  public data = input.required<Trial[]>();
  public isDisabled = input<boolean>(false);

  private ObjectiveCurrency: string;

  private _userSelectedTrial = signal<Trial | null>(null);
  private _summaryVersion = signal(0);

  protected SelectedTrial = computed(() => {
    const data = this.data();
    const userSelection = this._userSelectedTrial();
    if (!data || data.length === 0) return null;
    if (userSelection !== null) {
      if (data.includes(userSelection)) return userSelection;
      const matching = data.find(a => a.TrialNumber === userSelection.TrialNumber);
      if (matching) return matching;
    }
    return data[0];
  });
  protected isAssocDisabled = computed(() => this.SelectedTrial()?.RestrictionStatus == PaymentLineItemRestriction.NotAddedToInvoices);
  protected ShowRestrictionMessage = computed(() => this.SelectedTrial()?.RestrictionStatus == PaymentLineItemRestriction.NotAddedToInvoices);
  protected Summary = computed(() => {
    this._summaryVersion();
    return this.GetSummary();
  });

  private GetSummary() {
    const retVal = [];
    this.data().forEach((trial) => {
      retVal.push(...this.GetIndividualSummary(trial));
    });
    return retVal;
  }

  protected GetIndividualSummary(trial: Trial): TrialSummaryItem[] {
    const retVal: TrialSummaryItem[] = [];

    const trialSummaryRow: TrialSummaryItem = {
      Summary: "Trial",
      ObjectiveName: null,
      TrialNumber: trial.TrialNumber,
      Year: null,
      Committed: 0,
      Invoiced: 0
    }

    trial.BudgetYears.forEach((by) => {
      const yearSummaryRow: TrialSummaryItem = {
        Summary: "Year",
        Committed: 0,
        Invoiced: 0,
        ObjectiveName: null,
        TrialNumber: trial.TrialNumber,
        Year: by.Year
      }
      by.Objectives.forEach((objective) => {
        objective.Invoices.filter((inv) => inv.Editable).forEach((invoice) => {
          const hasTrialSummary = retVal.find((a) => a.Summary == "Trial" && a.TrialNumber === trial.TrialNumber);
          if (!hasTrialSummary)
            retVal.push(trialSummaryRow);

          const hasYearSummary = retVal.find((a) => a.Summary == "Year" && a.TrialNumber == trial.TrialNumber && a.Year == by.Year);
          if (!hasYearSummary)
            retVal.push(yearSummaryRow);

          trialSummaryRow.Committed += Number(objective.QuotedAmount);
          trialSummaryRow.Invoiced += Number(invoice.Amount);

          yearSummaryRow.Committed += Number(objective.QuotedAmount);
          yearSummaryRow.Invoiced += Number(invoice.Amount);

          retVal.push({
            Committed: objective.QuotedAmount || 0,
            Invoiced: invoice.Amount || 0,
            ObjectiveName: objective.ObjectiveName,
            Summary: "None",
            TrialNumber: trial.TrialNumber,
            Year: by.Year
          });
          this.ObjectiveCurrency = trial.Currency;
        });
      });
    });
    return retVal;
  }

  protected readonly COLUMNS = ['trial', 'committed', 'invoiced'] as const;

  protected selectTrial(trial: Trial) {
    this._userSelectedTrial.set(trial);
  }

  protected RefreshSummary() {
    this._summaryVersion.update(v => v + 1);
    this.dataChange.emit(this.data());
  }

  protected AddInvoice(objective: Objective, currency: string) {
    if (this.Summary().length == 0) this.ObjectiveCurrency = currency;

    objective.Invoices.unshift({
      Amount: 0,
      Name: this.invoiceNumber(),
      Editable: true,
      InvoiceId: this.invoiceId(),
      PaymentLineItemInvoiceAssocId: null,
      IsCurrentInvoice: true
    });

    this.RefreshSummary();
  }

  protected RemoveInvoice(objective: Objective, invoice: Invoice) {
    objective.Invoices.splice(objective.Invoices.indexOf(invoice), 1);
    this.RefreshSummary();
    if (this.Summary().length == 0) this.ObjectiveCurrency = null;
  }

  protected canAdd(objective: Objective) {
    return objective.Invoices.find((a) => a.Editable) === undefined && (this.HasSelectedCurrency() || this.ObjectiveCurrency == null);;
  }

  protected GetBudgetYearQuotedAmount(budgetYear: BudgetYear) {
    return budgetYear.Objectives.reduce((sum, objective) => sum + objective.QuotedAmount, 0);
  }

  protected GetLeft(budgetYear: BudgetYear) {
    let invoicedAmount = 0;
    budgetYear.Objectives.forEach(objective => objective.Invoices.forEach(inv => {
      invoicedAmount += inv.Amount;
    }));
    return this.GetBudgetYearQuotedAmount(budgetYear) - invoicedAmount;
  }

  private HasSelectedCurrency() {
    return this.SelectedTrial()?.Currency == this.ObjectiveCurrency
  }

  protected ShowCurrencyMessage() {
    return this.ObjectiveCurrency != null
      ? !this.isDisabled() && !this.HasSelectedCurrency()
      : false; 
  }
}
